import { useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Calendar,
  CheckCircle2,
  Circle,
  Eye,
  EyeOff,
  Layers,
  MoreVertical,
  PlayCircle,
  Settings,
  Trash2,
} from "lucide-react";

import { Avatar } from "../../../components/Avatar";
import { Progress } from "../../../components/Progress";
import { ReadOnlyCardTooltip } from "../../../components/ReadOnlyCardTooltip";
import { ReadOnlyBadge } from "../../../components/ReadOnlyBadge";
import { readOnlyCardSx } from "../../../utils/clientAccess";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/DropdownMenu";
import { getAvatarImageUrl } from "../../../utils/avatarUtils";
import { kanbanColors } from "../../../theme/theme";
import { useViewportBottomHeight } from "../../../hooks/useViewportBottomHeight";
import { PROJECT_STATE_BADGES } from "../stateBadges";

// Mismo lenguaje visual que el KanbanView de fases/tareas
const KANBAN_COLUMN_BG = kanbanColors.columnSurface;
// Aire bajo el tablero para que las columnas no queden pegadas al final de la página
const KANBAN_BOTTOM_GAP = 40;

const formatDate = (value) => {
  if (!value) return "Sin fecha";
  try {
    return new Date(value).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

function ProjectKanbanCardMenu({ project, interactionProps }) {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const {
    onStateChange,
    onDelete,
    onToggleVisibility,
    canToggleVisibility,
    onConfigureRecurrence,
  } = interactionProps;

  const hasMenuActions =
    Boolean(onStateChange) ||
    Boolean(onDelete) ||
    (Boolean(canToggleVisibility) && Boolean(onToggleVisibility)) ||
    Boolean(onConfigureRecurrence);

  if (!hasMenuActions) return null;

  const closeAnd = (action) => (event) => {
    event.stopPropagation();
    setMenuAnchorEl(null);
    action();
  };

  const stateItems = [
    { value: "not_started", icon: Circle },
    { value: "in_progress", icon: PlayCircle },
    { value: "completed", icon: CheckCircle2 },
  ];

  return (
    <>
      <Tooltip title="Ver acciones" arrow>
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            setMenuAnchorEl(event.currentTarget);
          }}
          sx={{ width: 24, height: 24, padding: 0, flexShrink: 0 }}
        >
          <MoreVertical size={16} />
        </IconButton>
      </Tooltip>
      <DropdownMenuContent
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        align="end"
        onClick={(event) => event.stopPropagation()}
      >
        {onStateChange &&
          stateItems
            .filter((item) => item.value !== project.state)
            .map(({ value, icon: Icon }) => (
              <DropdownMenuItem
                key={value}
                onClick={closeAnd(() => onStateChange(project.id, value, project.state))}
              >
                <Icon size={16} />
                {PROJECT_STATE_BADGES[value].label}
              </DropdownMenuItem>
            ))}
        {canToggleVisibility && onToggleVisibility && (
          <DropdownMenuItem
            onClick={closeAnd(() => onToggleVisibility(project.id, project.is_internal))}
          >
            {project.is_internal ? <Eye size={16} /> : <EyeOff size={16} />}
            {project.is_internal ? "Mostrar al cliente" : "Ocultar al cliente"}
          </DropdownMenuItem>
        )}
        {onConfigureRecurrence && (
          <DropdownMenuItem onClick={closeAnd(() => onConfigureRecurrence(project))}>
            <Settings size={16} />
            Configurar recurrencia
          </DropdownMenuItem>
        )}
        {onDelete && (
          <DropdownMenuItem
            onClick={closeAnd(() => onDelete(project.id))}
            sx={{ color: "error.main" }}
          >
            <Trash2 size={16} />
            Eliminar proyecto
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </>
  );
}

// Tarjeta compacta (3 líneas): título + menú, cliente con la fecha a la derecha,
// y plantilla con la barra de progreso a la derecha. Si no cabe, lo de la derecha baja.
function ProjectKanbanCard({ project, accent, interactionProps, onOpen, isDragging, dragProps }) {
  const theme = useTheme();
  const progress = Math.round(project.progress_percentage || 0);
  const isHiddenFromClient = project.is_internal && project.project_type === "custom";
  // Mientras el ratón está sobre los iconos de la cabecera la tarjeta deja de ser arrastrable
  const [isOverHeaderIcons, setIsOverHeaderIcons] = useState(false);
  const isDraggable = Boolean(dragProps.draggable) && !isOverHeaderIcons;
  const isReadOnly = Boolean(interactionProps.isReadOnly);
  const metaTextSx = {
    fontSize: "0.75rem",
    color: "text.secondary",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <ReadOnlyCardTooltip active={isReadOnly}>
      <Box
        {...dragProps}
        draggable={isDraggable}
        onClick={() => onOpen(project)}
        sx={{
          backgroundColor: theme.palette.background.paper,
          padding: "10px 12px 10px 14px",
          borderRadius: "10px",
          borderLeft: `3px solid ${accent}`,
          boxShadow: "0 1px 2px 0 rgba(17, 12, 34, 0.06)",
          cursor: isDraggable ? "grab" : "pointer",
          opacity: isDragging ? 0.45 : 1,
          transition: "box-shadow 0.2s, opacity 0.2s",
          "&:hover": { boxShadow: "0 6px 16px -6px rgba(17, 12, 34, 0.18)" },
          ...(isReadOnly && readOnlyCardSx(theme)),
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {project.name || project.template_name || "Proyecto"}
          </Typography>
          {/* Iconos centrados sobre la primera línea del título (1.3 × 0.875rem) */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            onMouseEnter={() => setIsOverHeaderIcons(true)}
            onMouseLeave={() => setIsOverHeaderIcons(false)}
            sx={{ height: "1.1375rem", flexShrink: 0, mr: -0.5 }}
          >
            {isHiddenFromClient && (
              <Tooltip title="Ocultado al cliente" arrow>
                <Box
                  onClick={(event) => event.stopPropagation()}
                  sx={{ display: "flex", color: "text.secondary", cursor: "default" }}
                >
                  <EyeOff size={13} />
                </Box>
              </Tooltip>
            )}
            <ProjectKanbanCardMenu project={project} interactionProps={interactionProps} />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          columnGap={1.5}
          rowGap={0.5}
          sx={{ mt: 0.75 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flex: "1 1 120px" }}>
            <Avatar
              size="sm"
              name={project.client_name || "Cliente"}
              src={getAvatarImageUrl(project.client_logo) || undefined}
            />
            <Typography sx={{ ...metaTextSx, color: "text.primary" }}>
              {project.client_name || "Sin cliente"}
            </Typography>
            {isReadOnly && <ReadOnlyBadge />}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Calendar size={12} color={theme.palette.text.secondary} />
            <Typography sx={{ ...metaTextSx, overflow: "visible" }}>
              {formatDate(project.start_date)}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          columnGap={1.5}
          rowGap={0.5}
          sx={{ mt: 0.75 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flex: "1 1 120px" }}>
            <Layers size={12} color={theme.palette.primary.main} style={{ flexShrink: 0 }} />
            <Typography sx={metaTextSx}>
              {project.template_name || "Sin plantilla"}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            <Box sx={{ width: 64 }}>
              <Progress value={project.progress_percentage || 0} />
            </Box>
            <Typography
              sx={{ fontSize: "0.75rem", fontWeight: 600, color: "primary.main", minWidth: 30, textAlign: "right" }}
            >
              {progress}%
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </ReadOnlyCardTooltip>
  );
}

ProjectKanbanCard.propTypes = {
  project: PropTypes.object.isRequired,
  accent: PropTypes.string.isRequired,
  interactionProps: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
  isDragging: PropTypes.bool,
  dragProps: PropTypes.object.isRequired,
};

/**
 * Vista Kanban del listado de proyectos: una columna por estado.
 * Arrastrar una tarjeta a otra columna dispara onStateChange (vía getProjectInteractionProps),
 * así que respeta los mismos permisos y la confirmación de "Completado" que el menú.
 */
export function ProjectsKanbanView({
  projects,
  columnCounts,
  isLoading,
  isLoadingMore,
  onViewProgress,
  getProjectInteractionProps,
}) {
  const theme = useTheme();
  const [draggedProject, setDraggedProject] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  // Las columnas llegan como mínimo hasta el final de la ventana, aunque haya pocos proyectos
  const [boardElement, setBoardElement] = useState(null);
  const fillHeight = useViewportBottomHeight(boardElement, KANBAN_BOTTOM_GAP);

  const columns = [
    { id: "not_started", title: "No iniciados", accent: "#9CA3AF" },
    { id: "in_progress", title: "En progreso", accent: theme.palette.primary.main },
    { id: "completed", title: "Completados", accent: "#0F9D58" },
  ];

  const resetDrag = () => {
    setDraggedProject(null);
    setDragOverColumn(null);
  };

  const handleDrop = (columnId) => (event) => {
    event.preventDefault();
    const project = draggedProject;
    resetDrag();
    if (!project || project.state === columnId) return;
    getProjectInteractionProps(project).onStateChange?.(project.id, columnId, project.state);
  };

  return (
    <Box
      ref={setBoardElement}
      sx={{
        display: "grid",
        // Columnas apiladas en móvil/tablet; en paralelo a partir de lg
        gridTemplateColumns: { xs: "1fr", lg: `repeat(${columns.length}, minmax(0, 1fr))` },
        gap: 2,
        // stretch: si una columna crece, las demás crecen con ella
        alignItems: "stretch",
        mb: `${KANBAN_BOTTOM_GAP}px`,
        minHeight: {
          lg: fillHeight ? `max(240px, ${fillHeight})` : 240,
        },
      }}
    >
      {columns.map((column) => {
        const columnProjects = projects.filter((project) => project.state === column.id);
        const isDropTarget =
          Boolean(draggedProject) &&
          draggedProject.state !== column.id &&
          dragOverColumn === column.id;

        return (
          <Box
            key={column.id}
            onDragOver={(event) => {
              if (!draggedProject) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              if (dragOverColumn !== column.id) setDragOverColumn(column.id);
            }}
            onDrop={handleDrop(column.id)}
            sx={{
              backgroundColor: KANBAN_COLUMN_BG,
              borderRadius: "16px",
              padding: { xs: "12px", sm: "14px" },
              minHeight: { xs: 96, lg: 0 },
              outline: isDropTarget ? `2px dashed ${column.accent}` : "2px dashed transparent",
              outlineOffset: "-2px",
              transition: "outline-color 0.15s",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, px: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: column.accent,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary" }}>
                {column.title}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                {columnCounts?.[column.id] ?? columnProjects.length}
              </Typography>
            </Stack>

            <Stack spacing={1}>
              {isLoading
                ? Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rounded"
                      height={92}
                      sx={{ borderRadius: "12px", bgcolor: "background.paper" }}
                    />
                  ))
                : columnProjects.map((project) => {
                    const interactionProps = getProjectInteractionProps(project);
                    const canDrag = Boolean(interactionProps.onStateChange);

                    return (
                      <ProjectKanbanCard
                        key={project.id}
                        project={project}
                        accent={column.accent}
                        interactionProps={interactionProps}
                        onOpen={onViewProgress}
                        isDragging={draggedProject?.id === project.id}
                        dragProps={{
                          draggable: canDrag,
                          ...(canDrag && {
                            onDragStart: (event) => {
                              event.dataTransfer.effectAllowed = "move";
                              event.dataTransfer.setData("text/plain", String(project.id));
                              setDraggedProject(project);
                            },
                            onDragEnd: resetDrag,
                          }),
                        }}
                      />
                    );
                  })}

              {!isLoading && columnProjects.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", textAlign: "center", py: 3 }}
                >
                  Sin proyectos
                </Typography>
              )}

              {isLoadingMore && (
                <Skeleton
                  variant="rounded"
                  height={92}
                  sx={{ borderRadius: "12px", bgcolor: "background.paper" }}
                />
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

ProjectKanbanCardMenu.propTypes = {
  project: PropTypes.object.isRequired,
  interactionProps: PropTypes.object.isRequired,
};

ProjectsKanbanView.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object).isRequired,
  columnCounts: PropTypes.object,
  isLoading: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  onViewProgress: PropTypes.func.isRequired,
  getProjectInteractionProps: PropTypes.func.isRequired,
};
